import React, { useState } from "react";
import { Field } from "formik";
import DashboardMainContainer from "../common/Containers/DashboardMainContainer";
import AcceptedPaymentsPopup from "./AcceptedPaymentsPopup";
import {
  AdminPanelTitle,
  AdminMetric,
  AdminMetadataText,
  AdminTableBody,
  AdminMainText,
} from "../../sections/AdminDashboard/shared/adminUx";

const AcceptedPaymentsWidget = ({ setFieldValue, values, errors }) => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [selectedFieldIndex, setSelectedFieldIndex] = useState(0);

  const paymentMethods = [
    { label: "Card", value: "card" },
    { label: "Apple Pay", value: "apple_pay" },
    { label: "Google Pay", value: "google_pay" },
    { label: "Afterpay", value: "afterpay" },
    { label: "Zip", value: "zip" },
    { label: "Klarna", value: "klarna" },
    { label: "PayID", value: "payid" },
    { label: "Bank Transfer", value: "bank_transfer" },
    { label: "Cash", value: "cash" },
  ];

  // Define fields with estimated completion times
  const fields = [
    { name: "Payment methods", key: "paymentMethods", estimatedTime: "2 min", required: true }
  ];

  // Sample data for demonstration
  const sampleData = {
    // All fields left empty intentionally
  };

  // Merge sample data with actual values (use sample data when form values are empty)
  const displayValues = {
    paymentMethods: values.paymentMethods || sampleData.paymentMethods || []
  };

  // Calculate completion percentage and estimated time
  const calculateProgress = () => {
    const completedFields = fields.filter(field => {
      const hasValue = displayValues[field.key] && displayValues[field.key].length > 0;
      return hasValue;
    }).length;
    
    return Math.round((completedFields / fields.length) * 100);
  };

  // Calculate estimated time for remaining fields
  const calculateEstimatedTime = () => {
    const remainingFields = fields.filter(field => {
      const hasValue = displayValues[field.key] && displayValues[field.key].length > 0;
      return !hasValue;
    });
    
    const totalMinutes = remainingFields.reduce((total, field) => {
      const timeStr = field.estimatedTime;
      const minutes = parseInt(timeStr.replace(' min', ''));
      return total + minutes;
    }, 0);
    
    return totalMinutes > 0 ? `Estimated ${totalMinutes} min` : 'All Completed';
  };

  const progress = calculateProgress();
  const estimatedTime = calculateEstimatedTime();

  // Get selected payment methods labels
  const getSelectedPaymentMethodsLabels = () => {
    const selectedValues = displayValues.paymentMethods || [];
    return selectedValues.map(value => {
      const option = paymentMethods.find(opt => opt.value === value);
      return option ? option.label : value;
    });
  };

  return (
    <>
      <DashboardMainContainer className="masonry-item w-[500px] h-auto account-widget">
        <div className="flex items-center justify-between py-3 px-4 border-b border-mediumgray">
          <AdminPanelTitle className="text-offblack">Accepted Payments</AdminPanelTitle>
          <div className="flex items-center gap-2">
            <div className="w-16 h-1.5 bg-lightgray rounded-full overflow-hidden">
              <div 
                className="h-full bg-freshgreen transition-all duration-500 ease-out"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
            <AdminMetric size="micro" className="text-offblack tabular-nums">
              {progress}%
            </AdminMetric>
          </div>
        </div>
        
        <div className="px-4 py-4">
          {/* Fields List */}
          <div className="space-y-0">
            {fields.map((field, index) => {
              const hasValue = displayValues[field.key] && displayValues[field.key].length > 0;
              const isCompleted = hasValue; // Only completed if it actually has a value
              
              return (
                <div key={field.key} className="flex items-center justify-between">
                    {progress === 100 ? (
                      <div 
                        className={`group flex items-center w-full py-[4px] cursor-pointer hover:text-primaryblue rounded px-2 -mx-2`}
                        onClick={() => {
                          setSelectedFieldIndex(index);
                          setIsPopupOpen(true);
                        }}
                      >
                        <AdminTableBody emphasis className="w-56 text-offblack group-hover:text-primaryblue">
                          {field.name}
                        </AdminTableBody>
                        <AdminMainText className="text-offblack group-hover:text-primaryblue truncate">
                          {isCompleted ? getSelectedPaymentMethodsLabels().join(", ") : `Estimated ${field.estimatedTime}`}
                        </AdminMainText>
                      </div>
                    ) : (
                    <>
                      <div 
                        className={`group flex items-center py-[4px] cursor-pointer hover:text-primaryblue rounded px-2 -mx-2 ${index < fields.length - 1 ? 'border-b border-transparent' : ''}`}
                        onClick={() => {
                          setSelectedFieldIndex(index);
                          setIsPopupOpen(true);
                        }}
                      >
                        <div className={`w-2 h-2 rounded-full mr-3 ${isCompleted ? 'bg-freshgreen' : 'bg-mediumgray'}`}></div>
                        <AdminTableBody emphasis className={`w-40 ${isCompleted ? 'text-offblack group-hover:text-primaryblue' : 'text-mediumgray group-hover:text-primaryblue'}`}>
                          {field.name}
                        </AdminTableBody>
                        <AdminMainText className={`text-offblack group-hover:text-primaryblue truncate max-w-[200px]`}>
                          {isCompleted ? getSelectedPaymentMethodsLabels().join(", ") : ''}
                        </AdminMainText>
                      </div>
                      <AdminMetadataText className="text-mediumgray">
                        {isCompleted ? 'Completed' : 'Start'}
                      </AdminMetadataText>
                    </>
                  )}
                </div>
              );
            })}
          </div>

        </div>
      </DashboardMainContainer>

      {/* Popup for editing */}
      {isPopupOpen && (
        <AcceptedPaymentsPopup
          isOpen={isPopupOpen}
          onClose={() => setIsPopupOpen(false)}
          setFieldValue={setFieldValue}
          values={values}
          errors={errors}
          businessDashboard={true}
          initialStep={selectedFieldIndex}
        />
      )}
    </>
  );
};

export default AcceptedPaymentsWidget;
